import { useContext, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'logic';
import {
  getProjectSearchScrollIntent,
  getProjectSearchScrollIntentStorageKey,
} from 'logic/project-search-scroll';
import type { ProjectTab } from './ProjectTabs';
import { ProjectTabContext } from './projectTabContextState';
import { getProjectTabFromRoute, navigateToProjectTab } from './projectTabRoutes';

export {
  getProjectTabFromRoute,
  getRouteFromProjectTab,
  getProjectTabRouteTarget,
} from './projectTabRoutes';

let hasWarnedMissingProjectTabProvider = false;

export const ProjectTabProvider = observer(({ children }: { children: ReactNode }) => {
  const rootStore = useStore();
  const { routerStore, sessionsStore } = rootStore;
  const sessionId = sessionsStore.currentSessionId;

  const activeTab = getProjectTabFromRoute(
    routerStore.getCurrentRoute()?.name,
    routerStore.routerState?.params ?? {},
  );

  useEffect(() => {
    if (!sessionId) {
      return undefined;
    }

    const handlePopState = () => {
      const intent = getProjectSearchScrollIntent({
        sessionId,
        pathname: window.location.pathname,
        source: 'browser-pop',
      });
      if (!intent) {
        return;
      }

      try {
        sessionStorage.setItem(getProjectSearchScrollIntentStorageKey(sessionId), intent);
      } catch (error) {
        console.warn('Failed to store Search scroll intent:', error);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [sessionId]);

  const setActiveTab = useCallback(
    (tab: ProjectTab, sourceId?: string) => {
      navigateToProjectTab(rootStore, tab, sourceId);
    },
    [rootStore],
  );

  const contextValue = useMemo(() => ({ activeTab, setActiveTab }), [activeTab, setActiveTab]);

  return <ProjectTabContext.Provider value={contextValue}>{children}</ProjectTabContext.Provider>;
});

export const useProjectTab = () => {
  const rootStore = useStore();
  const { routerStore } = rootStore;
  const context = useContext(ProjectTabContext);

  // Route is the source of truth — read it here so observer parents re-render on navigation.
  const activeTab = getProjectTabFromRoute(
    routerStore.getCurrentRoute()?.name,
    routerStore.routerState?.params ?? {},
  );

  const setActiveTab = useCallback(
    (tab: ProjectTab, sourceId?: string) => {
      navigateToProjectTab(rootStore, tab, sourceId);
    },
    [rootStore],
  );

  if (!context && !hasWarnedMissingProjectTabProvider) {
    hasWarnedMissingProjectTabProvider = true;
    console.warn(
      'useProjectTab called outside ProjectTabProvider — using route fallback (common after Vite HMR)',
    );
  }

  return { activeTab, setActiveTab };
};
